import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, Plus, ChevronLeft, ChevronRight, 
  Home, UserPlus, Trash2, RefreshCw, AlertCircle, ArrowRightLeft, Loader2, HeartHandshake,
} from 'lucide-react';
import api from '@/lib/axios';
import { footerToParams, getFooterSettings } from '@/components/FooterDateBar';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTenant } from '@/contexts/TenantContext';
import { Switch } from '@/components/ui/switch';
import Layout from '@/components/layout/Layout';
import { useActionBarConfig } from '@/contexts/ActionBarContext';
import { writeSelectedRecord } from '@/lib/selectedRecord';
import useListKeyboardNav from '@/hooks/useListKeyboardNav';
import useTableSort from '@/hooks/useTableSort';
import SortableTh from '@/components/table/SortableTh';

// Schlüssel müssen zu `sortable` in backend/routers/families.py passen.
const FAMILY_COLUMNS = [
  { key: 'name', label: 'Familie' },
  { key: 'address', label: 'Adresse' },
  { key: 'member_count', label: 'Mitglieder', center: true },
  { key: 'enrollment_count', label: 'Belegungen', center: true },
  { key: 'criterion', label: 'Kriterium', center: true },
];

export default function FamiliesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenants, activeTenantId } = useTenant();
  const [families, setFamilies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFamily, setSelectedFamily] = useState(null);

  useActionBarConfig({
    selected: selectedFamily ? { kind: 'family', id: selectedFamily.id, name: selectedFamily.name } : null,
    list: { kind: 'family', ids: families.map((f) => f.id) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFamily?.id, families]);

  useEffect(() => {
    if (selectedFamily?.id) writeSelectedRecord({ familyId: selectedFamily.id });
  }, [selectedFamily?.id]);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDetectDialog, setShowDetectDialog] = useState(false);
  const [detectMethod, setDetectMethod] = useState('guardian');
  const [detectLoading, setDetectLoading] = useState(false);
  const [detectResult, setDetectResult] = useState(null);
  const [sizeFilter, setSizeFilter] = useState(null);

  // Move dialog state
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveTargetTenantId, setMoveTargetTenantId] = useState('');
  const [moveEndEnrollments, setMoveEndEnrollments] = useState(true);
  const [movePreview, setMovePreview] = useState(null);
  const [movingState, setMovingState] = useState(null); // 'preview' | 'committing' | null

  const perPage = 20;
  const [footerTick, setFooterTick] = useState(0);
  useEffect(() => {
    const onFooter = () => {
      setPage(1);
      setFooterTick((n) => n + 1);
    };
    window.addEventListener('footer-settings-changed', onFooter);
    return () => window.removeEventListener('footer-settings-changed', onFooter);
  }, []);

  // Serverseitige Sortierung: Nur `sort`/`toggleSort` werden gebraucht, die
  // Reihenfolge liefert die API.
  const { sort, toggleSort } = useTableSort({}, { key: 'name' });
  const changeSort = (key) => {
    setPage(1);
    toggleSort(key);
  };

  const { getRowProps } = useListKeyboardNav({
    items: families,
    onSelect: (f) => openFamilyDetails(f),
    enabled: !showDetailsDialog && !showDetectDialog && !showMoveDialog,
  });

  const loadFamilies = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, per_page: perPage, search: searchTerm || undefined, ...footerToParams(getFooterSettings()) };
      if (sizeFilter) params.size = sizeFilter;
      // Serverseitig sortieren: im Browser liegt nur die aktuelle Seite.
      params.sort_by = sort.key;
      params.sort_dir = sort.dir;
      const response = await api.get('/families', { params });
      setFamilies(response.data.items);
      setTotalPages(Math.ceil(response.data.total / perPage));
    } catch (error) {
      toast.error('Fehler beim Laden der Familien');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, sizeFilter, sort, footerTick]);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/families/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  }, []);

  useEffect(() => {
    loadFamilies();
    loadStats();
  }, [loadFamilies, loadStats]);

  // Deep-Link aus Belegung etc.: /families?id=<uuid>
  useEffect(() => {
    const familyId = searchParams.get('id');
    if (!familyId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/families/${familyId}`);
        if (cancelled) return;
        setSelectedFamily(response.data);
        setShowDetailsDialog(true);
      } catch {
        if (!cancelled) toast.error('Familie konnte nicht geladen werden');
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete('id');
          setSearchParams(next, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('id')]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const openFamilyDetails = async (family) => {
    try {
      const response = await api.get(`/families/${family.id}`);
      setSelectedFamily(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Familiendetails');
    }
  };

  const toggleSocialDiscount = async (checked) => {
    if (!selectedFamily) return;
    try {
      await api.put(`/families/${selectedFamily.id}`, { social_discount_eligible: checked });
      setSelectedFamily((prev) => ({ ...prev, social_discount_eligible: checked }));
      toast.success(checked ? 'Sozialermäßigung aktiviert' : 'Sozialermäßigung deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Sozialermäßigung');
    }
  };

  const openMoveDialog = () => {
    setShowMoveDialog(true);
    setMovePreview(null);
    setMoveTargetTenantId('');
    setMoveEndEnrollments(true);
  };

  const previewMove = async () => {
    if (!selectedFamily || !moveTargetTenantId) {
      toast.error('Ziel-Mandant wählen');
      return;
    }
    setMovingState('preview');
    try {
      const res = await api.post(`/families/${selectedFamily.id}/move`, {
        target_tenant_id: moveTargetTenantId,
        end_active_enrollments: moveEndEnrollments,
        dry_run: true,
      });
      setMovePreview(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Vorschau fehlgeschlagen';
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setMovingState(null);
    }
  };

  const commitMove = async () => {
    if (!selectedFamily || !moveTargetTenantId) return;
    setMovingState('committing');
    try {
      const res = await api.post(`/families/${selectedFamily.id}/move`, {
        target_tenant_id: moveTargetTenantId,
        end_active_enrollments: moveEndEnrollments,
        dry_run: false,
      });
      const r = res.data;
      toast.success(
        `Familie umgezogen: ${r.moved_student_count} Schüler, ${r.moved_guardian_count} Guardians, ${r.cloned_guardian_count} geklont, ${r.ended_enrollments_count} Belegungen beendet`,
        { duration: 6000 },
      );
      setShowMoveDialog(false);
      setShowDetailsDialog(false);
      loadFamilies();
      loadStats();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Umzug fehlgeschlagen';
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Umzug.');
    } finally {
      setMovingState(null);
    }
  };

  const handleDetect = async (dryRun = true) => {
    try {
      setDetectLoading(true);
      const response = await api.post(`/families/detect?method=${detectMethod}&dry_run=${dryRun}`);
      setDetectResult(response.data);
      
      if (!dryRun) {
        toast.success(`${response.data.families_created} Familien erstellt`);
        loadFamilies();
        loadStats();
      }
    } catch (error) {
      toast.error('Fehler bei der Familienerkennung');
    } finally {
      setDetectLoading(false);
    }
  };

  const deleteFamily = async (familyId) => {
    if (!confirm('Familie wirklich löschen? Die Mitglieder werden nicht gelöscht.')) return;
    
    try {
      await api.delete(`/families/${familyId}`);
      toast.success('Familie gelöscht');
      loadFamilies();
      loadStats();
      setShowDetailsDialog(false);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="families-page">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1" data-testid="families-title">
              Familien
            </h1>
            <p className="text-slate-600">
              {stats
                ? `${stats.total_families} Familien · ${stats.students_in_families} gruppiert (${stats.grouping_rate}%)`
                : 'Familienverwaltung'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDetectDialog(true)}
            data-testid="families-detect-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Familien erkennen
          </Button>
        </div>

      {/* Stats Bar */}
      {stats && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-4 py-3 mb-4">
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <button
              type="button"
              onClick={() => { setSizeFilter(null); setPage(1); }}
              className={`px-3 py-1 rounded-md transition ${!sizeFilter ? 'bg-brand-red/10 text-brand-red ring-1 ring-brand-red/40' : 'hover:bg-slate-100'}`}
              data-testid="size-filter-all"
            >
              <span className="text-slate-500">Familien:</span>{' '}
              <span className="font-semibold">{stats.total_families}</span>
            </button>
            <div>
              <span className="text-slate-500">Gruppiert:</span>{' '}
              <span className="font-semibold">{stats.students_in_families}</span>
              <span className="text-slate-400 ml-1">({stats.grouping_rate}%)</span>
            </div>
            <div>
              <span className="text-slate-500">Ohne Familie:</span>{' '}
              <span className="font-semibold">{stats.students_without_family}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-500">Größenverteilung:</span>
              {Object.entries(stats.size_distribution || {})
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([size, count]) => {
                  const isActive = sizeFilter === size;
                  return (
                    <button
                      key={size}
                      type="button"
                      onClick={() => { setSizeFilter(isActive ? null : size); setPage(1); }}
                      className={`text-xs px-2 py-1 rounded border transition ${isActive ? 'bg-brand-red text-white border-brand-red' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                      data-testid={`size-filter-${size}`}
                    >
                      {size}er: {count}
                    </button>
                  );
                })}
            </div>
            {sizeFilter && (
              <button
                onClick={() => { setSizeFilter(null); setPage(1); }}
                className="text-xs text-brand-red hover:underline"
                data-testid="size-filter-clear"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div>
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Familie suchen..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <Home className="h-4 w-4 mr-2" />
            Startfenster
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-slate-50">
                {FAMILY_COLUMNS.map((col) => (
                  <SortableTh
                    key={col.key}
                    sortKey={col.key}
                    sort={sort}
                    onSort={changeSort}
                    testIdPrefix="families-sort"
                    className={`px-4 py-3 text-xs font-semibold text-slate-600 ${col.center ? 'text-center' : 'text-left'}`}
                  >
                    {col.label}
                  </SortableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-500">
                    Laden...
                  </td>
                </tr>
              ) : families.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-500">
                    Keine Familien gefunden
                  </td>
                </tr>
              ) : (
                families.map((family, idx) => {
                  // Parse migration marker from notes (e.g. "Mandant gewechselt am 2026-04-29 von MA-GRE")
                  const migrationMatch = family.notes && family.notes.match(/Mandant gewechselt(?:: ([A-Z-]+) → [A-Z-]+)?(?: am (\d{4}-\d{2}-\d{2}))?(?: von ([A-Z-]+))?/);
                  const migration = migrationMatch ? {
                    fromSlug: migrationMatch[3] || migrationMatch[1] || '?',
                    date: migrationMatch[2] || null,
                  } : null;
                  return (
                  <tr
                    key={family.id}
                    className="border-b hover:bg-brand-red/5 cursor-pointer transition-colors data-[keyboard-selected=true]:bg-brand-red/10 data-[keyboard-selected=true]:ring-2 data-[keyboard-selected=true]:ring-brand-red/30 data-[keyboard-selected=true]:ring-inset"
                    data-testid={`family-row-${family.id}`}
                    {...getRowProps(idx, family)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800 flex items-center gap-2">
                        {family.name}
                        {family.social_discount_eligible && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold border border-amber-200"
                            title="Sozialermäßigung aktiv — Rabatt greift automatisch bei neuen Verträgen"
                            data-testid={`social-badge-${family.id}`}
                          >
                            <HeartHandshake className="w-3 h-3" />
                            Sozialermäßigung
                          </span>
                        )}
                        {migration && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-brand-red/10 text-brand-red font-semibold border border-brand-red/20"
                            title={`Zugezogen von ${migration.fromSlug}${migration.date ? ` am ${migration.date}` : ''}`}
                            data-testid={`migration-badge-${family.id}`}
                          >
                            <ArrowRightLeft className="w-3 h-3" />
                            zugezogen aus {migration.fromSlug}
                          </span>
                        )}
                      </div>
                      {family.primary_email && (
                        <div className="text-xs text-slate-500">{family.primary_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {family.primary_address && (
                        <div>{family.primary_address}</div>
                      )}
                      {family.primary_postal_code && family.primary_city && (
                        <div>{family.primary_postal_code} {family.primary_city}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant="secondary">{family.member_count}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={family.active_enrollments_count > 0 ? "default" : "outline"}>
                        {family.active_enrollments_count}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant="outline" className="text-xs">
                        {family.match_criteria || '-'}
                      </Badge>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-slate-500">
              Seite {page} von {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Family Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedFamily?.name}</DialogTitle>
            <DialogDescription>
              {selectedFamily?.member_count} Mitglieder • {selectedFamily?.active_enrollments_count} aktive Belegungen
            </DialogDescription>
          </DialogHeader>
          
          {selectedFamily && (
            <div className="space-y-4">
              {/* Address */}
              {selectedFamily.primary_address && (
                <div className="text-sm">
                  <div className="font-medium text-slate-500 mb-1">Adresse</div>
                  <div>{selectedFamily.primary_address}</div>
                  <div>{selectedFamily.primary_postal_code} {selectedFamily.primary_city}</div>
                </div>
              )}

              {/* iMikel Kassenzeichen */}
              {(selectedFamily.imikel_kassenzeichen || (selectedFamily.imikel_hm_kassenzeichen?.length > 0)) && (
                <div className="text-sm" data-testid="family-detail-kassenzeichen">
                  <div className="font-medium text-slate-500 mb-1">iMikel Kassenzeichen</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedFamily.imikel_kassenzeichen && (
                      <Badge variant="outline" className={
                        selectedFamily.imikel_kassenzeichen.toUpperCase().startsWith('TA')
                          ? 'border-brand-red/30 bg-brand-red/5 text-brand-red font-mono'
                          : 'border-amber-300 bg-amber-50 text-amber-700 font-mono'
                      } title={selectedFamily.imikel_kassenzeichen.toUpperCase().startsWith('TA')
                        ? 'Übergeordnetes Kassenzeichen (Unterrichtsvertrag)'
                        : 'Instrumentenmietvertrag (HM.) — keine Unterrichtsbelegung vorhanden'}>
                        {selectedFamily.imikel_kassenzeichen}
                      </Badge>
                    )}
                    {(selectedFamily.imikel_hm_kassenzeichen || []).map((hm) => (
                      <Badge key={hm} variant="outline" className="border-slate-300 bg-slate-50 text-slate-600 font-mono text-xs" title="Untergeordneter Instrumentenmietvertrag">
                        {hm}
                      </Badge>
                    ))}
                  </div>
                  {selectedFamily.imikel_hm_kassenzeichen?.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1 italic">
                      {selectedFamily.imikel_hm_kassenzeichen.length} weitere{selectedFamily.imikel_hm_kassenzeichen.length === 1 ? 'r' : ''} Instrumentenmietvertrag unter diesem Haushalt
                    </p>
                  )}
                </div>
              )}
              
              {/* Sozialermäßigung */}
              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100 rounded-lg" data-testid="family-social-discount-row">
                <div>
                  <div className="font-medium text-slate-900 text-sm">Sozialermäßigung</div>
                  <div className="text-xs text-slate-500">
                    Rabatt greift automatisch bei neuen Verträgen (Prozentsatz je Mandant in den Einstellungen)
                  </div>
                </div>
                <Switch
                  checked={!!selectedFamily.social_discount_eligible}
                  onCheckedChange={toggleSocialDiscount}
                  data-testid="family-social-discount-switch"
                />
              </div>

              {/* Members */}
              <div>
                <div className="font-medium text-slate-500 mb-2">Mitglieder</div>
                <div className="space-y-2">
                  {selectedFamily.members?.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-slate-50 hover:bg-brand-red/5 rounded-lg cursor-pointer transition-colors"
                      onClick={() => {
                        setShowDetailsDialog(false);
                        navigate(`/students/${member.id}`);
                      }}
                      data-testid={`family-member-row-${member.id}`}
                    >
                      <div>
                        <div className="font-medium">
                          {member.first_name} {member.last_name}
                        </div>
                        <div className="text-sm text-slate-500">
                          {member.email}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.active_enrollments > 0 && (
                          <Badge>{member.active_enrollments} Belegungen</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={openMoveDialog}
              data-testid="family-move-btn"
              className="text-brand-red border-brand-red/30 hover:bg-brand-red/5"
            >
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Mandant umziehen
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => deleteFamily(selectedFamily?.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Familie löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Family Move Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-xl" data-testid="family-move-dialog">
          <DialogHeader>
            <DialogTitle>Familie auf anderen Mandanten umziehen</DialogTitle>
            <DialogDescription>
              {selectedFamily?.name} · {selectedFamily?.member_count} Mitglieder ·{' '}
              {selectedFamily?.active_enrollments_count} aktive Belegungen
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Ziel-Mandant
              </label>
              <Select value={moveTargetTenantId} onValueChange={setMoveTargetTenantId}>
                <SelectTrigger data-testid="move-target-tenant">
                  <SelectValue placeholder="— Mandant wählen —" />
                </SelectTrigger>
                <SelectContent>
                  {tenants
                    .filter((t) => t.id !== activeTenantId && t.id !== selectedFamily?.tenant_id)
                    .map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} ({t.slug})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border border-amber-200 bg-brand-red/5">
              <input
                type="checkbox"
                checked={moveEndEnrollments}
                onChange={(e) => setMoveEndEnrollments(e.target.checked)}
                data-testid="move-end-enrollments"
              />
              <span>
                <strong>Aktive Belegungen beenden</strong> — Kurse am Quell-Mandanten werden auf{' '}
                <code className="text-xs">status='ended'</code> gesetzt (kein automatischer Wiedereintritt am Ziel-Mandanten).
              </span>
            </label>

            {!movePreview && (
              <Button
                onClick={previewMove}
                disabled={!moveTargetTenantId || movingState === 'preview'}
                variant="outline"
                className="w-full"
                data-testid="move-preview-btn"
              >
                {movingState === 'preview' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Vorschau berechnen
              </Button>
            )}

            {movePreview && (
              <div className="space-y-2 p-4 rounded-md bg-slate-50 border border-slate-200 text-sm" data-testid="move-preview">
                <div className="flex justify-between">
                  <span className="text-slate-600">Quelle</span>
                  <span className="font-mono">{movePreview.source_tenant_slug}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Ziel</span>
                  <span className="font-mono font-semibold">{movePreview.target_tenant_slug}</span>
                </div>
                <hr className="border-slate-200" />
                <div className="flex justify-between">
                  <span className="text-slate-600">Schüler</span>
                  <span className="font-medium">{movePreview.student_count} ({movePreview.student_names.join(', ')})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Guardians (verschieben)</span>
                  <span className="font-medium">
                    {movePreview.guardians_to_move.length}
                    {movePreview.guardians_to_move.length > 0 && (
                      <span className="ml-1 text-xs text-slate-500">({movePreview.guardians_to_move.join(', ')})</span>
                    )}
                  </span>
                </div>
                {movePreview.guardians_to_clone.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-amber-700">Guardians (klonen)</span>
                    <span className="font-medium text-amber-700" title="Diese Guardians sind mit weiteren Familien im Quell-Mandant verknüpft und werden für die Zielfamilie geklont">
                      {movePreview.guardians_to_clone.length} ({movePreview.guardians_to_clone.join(', ')})
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-600">SEPA-Mandate mitnehmen</span>
                  <span className="font-medium">{movePreview.sepa_mandates_carried}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Aktive Belegungen</span>
                  <span className="font-medium">
                    {movePreview.active_enrollments_count}
                    {movePreview.end_active_enrollments && movePreview.active_enrollments_count > 0 && (
                      <span className="ml-1 text-xs text-rose-700">→ werden beendet</span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowMoveDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={commitMove}
              disabled={!movePreview || movingState === 'committing'}
              className="bg-brand-red hover:bg-brand-red/90"
              data-testid="move-commit-btn"
            >
              {movingState === 'committing' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Umzug durchführen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detect Families Dialog */}
      <Dialog open={showDetectDialog} onOpenChange={setShowDetectDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Familien automatisch erkennen</DialogTitle>
            <DialogDescription>
              Das System erkennt Familien anhand gemeinsamer Daten wie Adresse, Telefon oder Erziehungsberechtigte.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Erkennungsmethode</label>
              <Select value={detectMethod} onValueChange={setDetectMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="guardian">Gemeinsamer Erziehungsberechtigter (empfohlen)</SelectItem>
                  <SelectItem value="address">Gleiche Adresse</SelectItem>
                  <SelectItem value="phone">Gleiche Telefonnummer</SelectItem>
                  <SelectItem value="all">Alle Kriterien kombiniert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {detectResult && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-blue-500" />
                  <span className="font-medium">Vorschau</span>
                </div>
                <div className="text-sm space-y-1">
                  <div>Erkannte Familien: <strong>{detectResult.details?.length || 0}</strong></div>
                  <div>Schüler gruppiert: <strong>{detectResult.students_grouped}</strong></div>
                </div>
                {detectResult.details?.slice(0, 5).map((d, i) => (
                  <div key={i} className="text-xs text-slate-600 mt-2">
                    • {d.family_name}: {d.members?.slice(0, 3).join(', ')}{d.members?.length > 3 ? '...' : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => handleDetect(true)}
              disabled={detectLoading}
            >
              {detectLoading ? 'Analysiere...' : 'Vorschau'}
            </Button>
            <Button
              onClick={() => handleDetect(false)}
              disabled={detectLoading || !detectResult}
            >
              {detectLoading ? 'Erstelle...' : 'Familien erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
